import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "../../../layout/components/Header";
import TenantLogo from "../../../layout/components/TenantLogo";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { Button } from "../../../../components/ui/button";
import BookLocationHeader from "../../common/util/BookLocationHeader";
import KeyHandoverNavigateBackAction from "../common/util/KeyHandoverNavigateBackAction";
import GeneralDialog from "../../../common/GeneralDialog";
import routes from "../../../../routes/routes";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { bookingCreationCompleted } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

interface KeyHandoverPageLayoutProps {
  /** Page title/header text */
  headerText: string;
  /** Back navigation target route (optional - omit for first page) */
  backTo?: string;
  /** Main content */
  children: ReactNode;
  /** Optional bottom actions (e.g., Next button) */
  bottomActions?: ReactNode;
  /** Hide back button even if backTo is provided */
  hideBackButton?: boolean;
}

/**
 * Shared layout component for all Key Handover flow pages.
 * 
 * @remarks
 * Provides consistent:
 * - Header with back button, logo, and close button
 * - Page title
 * - Content area with max-width container
 * - Optional bottom action buttons
 * - Close confirmation dialog
 * 
 * @example
 * ```tsx
 * <KeyHandoverPageLayout
 *   headerText={t("book_location.keys.schedule.header")}
 *   backTo={routes.keyHandoverServiceSelectionPath}
 *   bottomActions={<NextButton />}
 * >
 *   <ScheduleContent />
 * </KeyHandoverPageLayout>
 * ```
 */
export const KeyHandoverPageLayout: React.FC<KeyHandoverPageLayoutProps> = ({
  headerText,
  backTo,
  children,
  bottomActions,
  hideBackButton = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  const handleClose = () => {
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          {backTo && !hideBackButton && <KeyHandoverNavigateBackAction to={backTo} />}
        </div>
        <TenantLogo />
        <Button
          id="closeButton"
          variant="ghost"
          size="icon"
          onClick={() => setCloseDialogOpen(true)}
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full text-foreground/90"
        >
          <CloseIcon />
        </Button>
      </Header>

      <section className="flex-1">
        <div className="max-w-[1000px] mx-auto px-4">
          {headerText && <BookLocationHeader text={headerText} />}
          <div className="mt-6">{children}</div>
        </div>
      </section>

      {bottomActions && (
        <div
          className="max-w-[1000px] mx-auto w-full pb-10 px-4"
          style={{ paddingBottom: "calc(40px + env(safe-area-inset-bottom, 0))" }}
        >
          {bottomActions}
        </div>
      )}

      <GeneralDialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        onContinue={handleClose}
        content={t("use_order.closing_dialog.content")}
      />
    </div>
  );
};
